import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import moment from 'moment/moment';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import { useApi } from '../../ContextApi/ContextApi';
import './Template7.css';

const Template7 = ({ reportData, QRurl, signatories }) => {
  const [FontStyle, setFontStyles] = useState([]);
  const [elecSigMsg, setElecSigMsg] = useState('');
  const { Comment, isheader } = useApi();

  const currentDateTime = new Date();
  const username = (sessionStorage.getItem('FirstName') || '') + (sessionStorage.getItem('LastName') || '');

  // Helper function to format UTC or server date strings to local browser time
  const formatLocalTime = (dateInput, formatPattern = 'DD-MMM-YYYY hh:mm A') => {
    if (!dateInput) return '-';
    
    // If dateInput is a string and doesn't end with Z or timezone offset, append 'Z' to treat as UTC
    let formattedInput = dateInput;
    if (typeof dateInput === 'string' && !dateInput.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateInput)) {
      formattedInput = `${dateInput}Z`;
    }

    return moment.utc(formattedInput).local().format(formatPattern);
  };

  useEffect(() => {
    try {
      const fonts = JSON.parse(localStorage.getItem('FontStyle'));
      if (fonts) {
        setFontStyles(fonts);
        if (fonts.electronicSignatureMsg) {
          setElecSigMsg(fonts.electronicSignatureMsg);
        }
      }
    } catch (error) {
      console.error('Error loading FontStyle from localStorage:', error);
    }
  }, []);

  const image = localStorage.getItem('LogoImage');
  const qrCodeValue = QRurl;

  const groupByCategoryAndSubCategory = (data) => {
    return data.reduce((result, item) => {
      const category = item.categorY_CODE;
      const subCategory = item.suB_CATEGORY_CODE;

      if (!result[category]) {
        result[category] = {
          categoryValue: item.categorY_VALUE,
          subCategories: {},
        };
      }

      if (!result[category].subCategories[subCategory]) {
        result[category].subCategories[subCategory] = {
          subCategoryValue: item.suB_CATEGORY_VALUE,
          items: [],
        };
      }

      result[category].subCategories[subCategory].items.push(item);
      return result;
    }, {});
  };

  const groupedData = groupByCategoryAndSubCategory(reportData || []);

  const formatTextWithLineBreaks = (text) => {
    if (!text) return '';
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const patient = reportData?.[0];
  const labName = reportData?.find((x) => x.laboratoryName !== '' && x.laboratoryName != null)?.laboratoryName;
  const patientNo = patient?.patientID || '-';
  const trId = patient?.trId || patient?.testRequestID || '-';
  const barcodeRef = patient?.barcodeRef || `${moment(currentDateTime).format('YYMMDD')}-1245-001-15`;

  const bodyFont = FontStyle?.fontfamily ? FontStyle?.fontfamily : 'Arial, sans-serif';
  const bodyFontSize = FontStyle?.fontsize ? FontStyle?.fontsize : '11px';

  return (
    <>
      {/* ================= TOP GRADIENT BAR ================= */}
      <div className="t7-top-bar" />

      {/* ================= HEADER ================= */}
      <div className="t7-header">
        {isheader ? (
          <Grid container alignItems="center" className="t7-header-row">
            <Grid item xs={6} className="t7-logo-col">
              {image ? (
                <img className="t7-logo-img" src={'data:image/png;base64,' + image} alt="Lab Logo" />
              ) : (
                <div className="t7-logo-fallback">
                  <BloodtypeIcon className="t7-logo-icon" />
                  <div>
                    <div className="t7-lab-name">{labName || 'LABORATORY NAME'}</div>
                    <div className="t7-lab-tagline">Committed To Excellent Diagnosis.....!</div>
                  </div>
                </div>
              )}
            </Grid>

            <Grid item xs={3} className="t7-ids-col">
              <div className="t7-id-row">
                <span className="t7-id-label">Patient No:</span>
                <span className="t7-id-value">{patientNo}</span>
              </div>
              <Barcode value={String(patientNo)} height={16} width={0.9} fontSize={0} margin={0} displayValue={false} />
              <div className="t7-id-row" style={{ marginTop: '2px' }}>
                <span className="t7-id-label">T/R ID:</span>
                <span className="t7-id-value">{trId}</span>
              </div>
              <Barcode value={String(barcodeRef)} height={16} width={0.9} fontSize={8} margin={0} displayValue={true} />
            </Grid>

            <Grid item xs={3} className="t7-qr-col">
              <QRCode value={qrCodeValue} size={65} />
            </Grid>
          </Grid>
        ) : ''}
        <hr className="t7-header-divider" />
      </div>

      {/* ================= PATIENT INFO GRID ================= */}
      <div className="t7-patient-grid" style={{ fontFamily: bodyFont, fontSize: bodyFontSize }}>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Patient Name</span>
          <span className="t7-pg-value">{patient?.patientName}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Phone #</span>
          <span className="t7-pg-value">{patient?.contactNumber}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Referred By</span>
          <span className="t7-pg-value">{patient?.referredBy || 'Self'}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Registration Date/Time</span>
          <span className="t7-pg-value">{formatLocalTime(patient?.createdAt)}</span>
        </div>

        <div className="t7-pg-cell">
          <span className="t7-pg-label">S/O D/O W/O</span>
          <span className="t7-pg-value">{patient?.guardianName || '-'}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">CNIC #</span>
          <span className="t7-pg-value">{patient?.cnic || '-'}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Consultant</span>
          <span className="t7-pg-value">{patient?.consultant || '-'}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Registration Location</span>
          <span className="t7-pg-value">{patient?.registrationLocation || 'Head Office'}</span>
        </div>

        <div className="t7-pg-cell">
          <span className="t7-pg-label">Age/Gender</span>
          <span className="t7-pg-value">{patient?.age} / {patient?.sex}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Address</span>
          <span className="t7-pg-value">{patient?.address || '-'}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Collection Type</span>
          <span className="t7-pg-value">{patient?.collectionType || 'Taken in lab'}</span>
        </div>
        <div className="t7-pg-cell">
          <span className="t7-pg-label">Reporting Location</span>
          <span className="t7-pg-value">{patient?.reportingLocation || 'Head Office'}</span>
        </div>
      </div>
      <hr className="t7-header-divider" />

      {/* ================= REPORT SECTIONS ================= */}
      <div className="t7-content" style={{ fontFamily: bodyFont, fontSize: bodyFontSize }}>
        {Object.keys(groupedData).map((category, index) => {
          const subCategoryKeys = Object.keys(groupedData[category].subCategories);
          const firstItem = groupedData[category].subCategories[subCategoryKeys[0]]?.items?.[0];

          return (
            <div key={index} className="t7-category-block">
              <div className="t7-category-head-row">
                <div className="t7-category-title">{groupedData[category].categoryValue}</div>
              </div>

              {subCategoryKeys.map((subCategory, subIndex) => (
                <TableContainer key={subIndex} className="t7-table-container t7-table-overflow-visible">
                  <Table size="small" className="t7-table">
                    <TableHead>
                      <TableRow className="t7-table-head-row">
                        <TableCell className="t7-th">Test</TableCell>
                        <TableCell className="t7-th">Normal Range</TableCell>
                        <TableCell className="t7-th">Unit</TableCell>
                        <TableCell className="t7-th t7-th-result t7-th-extended">
                          <div className="t7-result-tab-extended">
                            <span className="t7-result-title">Result</span>
                            <div className="t7-result-barcode-wrapper">
                              <Barcode
                                value={String(firstItem?.resultId || patientNo)}
                                height={12}
                                width={0.75}
                                fontSize={7}
                                margin={0}
                                displayValue={true}
                              />
                            </div>
                            <span className="t7-result-date">
                              {formatLocalTime(firstItem?.resultDateTime || currentDateTime)}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedData[category].subCategories[subCategory].items.map((data, dIndex) => (
                        <React.Fragment key={dIndex}>
                          <TableRow className="t7-data-row">
                            <TableCell>{data.testName}</TableCell>
                            <TableCell>{formatTextWithLineBreaks(data.normalRange)}</TableCell>
                            <TableCell>{formatTextWithLineBreaks(data.unit)}</TableCell>
                            <TableCell className={data.isAbnormal ? 't7-result-abnormal' : 't7-result-cell'}>
                              {data.testResult}
                            </TableCell>
                          </TableRow>

                          {data.method && (
                            <TableRow className="t7-note-row">
                              <TableCell colSpan={4} className="t7-note-cell">
                                <strong>Method:</strong> {data.method} {data.methodDescription && ` - ${data.methodDescription}`}
                              </TableCell>
                            </TableRow>
                          )}

                          {data.referenceIntervals && (
                            <TableRow className="t7-note-row">
                              <TableCell colSpan={4} className="t7-note-cell">
                                <strong>Reference Intervals:</strong> {formatTextWithLineBreaks(data.referenceIntervals)}
                              </TableCell>
                            </TableRow>
                          )}

                          {data.comment && (
                            <TableRow className="t7-note-row">
                              <TableCell colSpan={4} className="t7-comment-cell">
                                <strong>Comment:</strong> {data.comment}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ))}
            </div>
          );
        })}

        {Comment && (
          <div className="t7-interpretation-box">
            <span className="t7-interpretation-label">Interpretation:</span> {Comment}
          </div>
        )}
      </div>

      {/* ================= ELECTRONIC SIGNATURE DISCLAIMER ================= */}
      {elecSigMsg && (
        <div className="t7-elec-signature-container" style={{ fontFamily: bodyFont }}>
          <p className="t7-elec-signature-text">{elecSigMsg}</p>
        </div>
      )}

      {/* ================= FOOTER CONTACT BAR ================= */}
      <div className="t7-footer-bar">
        <span><PhoneIcon fontSize="inherit" className="t7-footer-icon" /> {patient?.laboratoryContactNumber || '0922-515515'}</span>
        <span><WhatsAppIcon fontSize="inherit" className="t7-footer-icon" /> {patient?.laboratoryContactNumber || '0922-515515'}</span>
        <span><LocationOnIcon fontSize="inherit" className="t7-footer-icon" /> {patient?.laboratoryAddress || 'Head Office: Medicine Market Near DHQ Hospital KDA Kohat'}</span>
      </div>

      <div className="t7-footer-meta" style={{ fontFamily: bodyFont }}>
        <span>Printed at: {moment().format('DD-MMM-YYYY hh:mm A')}</span>&nbsp;&nbsp;<span>Printed By: {username}</span>
      </div>
    </>
  );
};

export default Template7;